// src/pages/PublicQuotePage.js
import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import api from '../utils/api';
import Loader from '../components/common/Loader';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { toZonedTime, format } from 'date-fns-tz';
import Modal from '../components/common/Modal';
import { 
    Check, X, FileText, Calendar, Clock, AlertTriangle, Info, CheckCircle, MapPin,
    PoundSterling, DollarSign, Euro, JapaneseYen, Download, Timer, CreditCard,
    Sparkles // ⭐ NEW: Quote Add-ons
} from 'lucide-react';

const PublicQuotePage = () => {
    const { token } = useParams();

    const [quote, setQuote] = useState(null);
    const [companySettings, setCompanySettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
    const [declineReason, setDeclineReason] = useState('');
    const [actionOutcome, setActionOutcome] = useState(null);

    // ⭐ NEW: Quote Add-ons. Optional extras the customer can tick before pressing Accept. The
    // server re-checks everything and uses its own prices, so this is only the live preview.
    const [selectedAddOnIds, setSelectedAddOnIds] = useState([]);
    const toggleAddOn = (id) => {
        const key = String(id);
        setSelectedAddOnIds(prev => (prev.includes(key) ? prev.filter(x => x !== key) : [...prev, key]));
    };

    const companyTimezone = companySettings?.timezone || 'UTC';
    
    // ⭐ NEW: Detect US Timezone
    const isUS = useMemo(() => companyTimezone.startsWith('America/'), [companyTimezone]);

    // ⭐ NEW: Dynamic Date Formatting Helper
    const formatDate = useMemo(() => (dateString) => {
        if (!dateString) return 'N/A';
        const zonedDate = toZonedTime(new Date(dateString), companyTimezone);
        const formatString = isUS ? 'MMM do yyyy' : 'do MMMM yyyy';
        return format(zonedDate, formatString, { timeZone: companyTimezone });
    }, [isUS, companyTimezone]);

    const formatCurrency = (amount) => {
        if (!companySettings || !companySettings.currency) {
            return `£${Number(amount)?.toFixed(2) || '0.00'}`;
        }

        if (typeof amount === 'number') {
            const { symbol, decimalPlaces, thousandSeparator, decimalSeparator, formatTemplate, code } = companySettings.currency;
            const fixedAmount = amount.toFixed(decimalPlaces);
            const parts = fixedAmount.split('.');
            parts[0] = parts[0].replace(new RegExp(`\\B(?=(\\d{3})+(?!\\d))`, 'g'), thousandSeparator);
            const formattedAmount = parts.join(decimalSeparator);

            return formatTemplate
                .replace('{symbol}', symbol)
                .replace('{amount}', formattedAmount)
                .replace('{code}', code);
        }
        return `£${Number(amount)?.toFixed(2) || '0.00'}`;
    };

    // ⭐ NEW: Duration Formatting Helper
    const formatDuration = (totalMinutes) => {
        if (!totalMinutes) return null;
        const d = parseInt(totalMinutes, 10);
        if (d <= 0) return null;
        const hrs = Math.floor(d / 60);
        const mins = d % 60;
        const hrText = hrs > 0 ? `${hrs} hr${hrs > 1 ? 's' : ''}` : '';
        const minText = mins > 0 ? `${mins} min${mins > 1 ? 's' : ''}` : '';
        return [hrText, minText].filter(Boolean).join(' ');
    };

    // Helper function to get the correct icon based on currency code
    const getCurrencyIcon = () => {
        const code = companySettings?.currency?.code;
        switch (code) {
            case 'GBP':
                return <PoundSterling />;
            case 'EUR':
                return <Euro />;
            case 'JPY':
                return <JapaneseYen />;
            // Default for USD and others
            default:
                return <DollarSign />;
        }
    };

    useEffect(() => {
        if (!token) {
            setError('No quote token provided. This link may be invalid.');
            setLoading(false);
            return;
        }

        const fetchPublicQuote = async () => {
            setLoading(true);
            try {
                const res = await api.get(`/public/quotes/${token}`);
                setQuote(res.data.quote);
                setCompanySettings(res.data.companySettings);
                
                const currentStatus = res.data.quote.status;
                if (currentStatus !== 'pending_quote' && currentStatus !== 'quote_sent') {
                    // Check for standard accepted strings OR any auto-booked job statuses
                    const isAcceptedOrBooked = currentStatus === 'quote_accepted' || currentStatus === 'scheduled' || currentStatus === 'pending_deposit';
                    
                    setActionOutcome(isAcceptedOrBooked ? 'accepted' : currentStatus.includes('decline') ? 'declined' : 'expired');
                }
            } catch (err) {
                const errorMessage = err.response?.data?.message || 'Failed to load quote details. The link might be expired or invalid.';
                setError(errorMessage);
                toast.error(errorMessage);
            } finally {
                setLoading(false);
            }
        };

        fetchPublicQuote();
    }, [token]);

    const handleAcceptQuote = async () => {
        setSubmitting(true);
        try {
            // ⭐ NEW: send the optional extras the customer ticked
            const res = await api.put(`/public/quotes/${token}/accept`, { addOnIds: selectedAddOnIds });
            
            // ⭐ NEW: Handle Stripe Payment Link Redirect
            if (res.data.paymentLink) {
                toast.info('Quote accepted! Redirecting to secure payment...', { autoClose: 2000 });
                // Briefly wait so the user sees the success message before jumping to Stripe
                setTimeout(() => {
                    window.location.href = res.data.paymentLink;
                }, 1500);
            } else {
                toast.success(res.data.message || 'Quote accepted successfully!');
                setQuote(res.data.job);
                setActionOutcome('accepted');
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to accept quote.');
            setSubmitting(false); // Only toggle false if it failed. If success/redirecting, keep it true so buttons stay disabled
        }
    };

    const handleDeclineSubmit = async () => {
        setSubmitting(true);
        try {
            const res = await api.put(`/public/quotes/${token}/decline`, { reason: declineReason });
            toast.info(res.data.message || 'Quote declined.');
            setQuote(res.data.job);
            setIsDeclineModalOpen(false);
            setDeclineReason('');
            setActionOutcome('declined');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to decline quote.');
        } finally {
            setSubmitting(false);
        }
    };

    // ⭐ NEW: Quote Add-ons live totals (only on a fixed price, not yet actioned quote)
    const availableExtras = (quote && quote.payType !== 'Hourly Rate' && !(quote.usedStockItems && quote.usedStockItems.length)
        && ['pending_quote', 'quote_sent'].includes(quote.status) && Array.isArray(quote.quoteAddOns))
        ? quote.quoteAddOns.filter(a => a && !a.selected)
        : [];
    const extrasTotal = Math.round(availableExtras
        .filter(a => selectedAddOnIds.includes(String(a._id)))
        .reduce((sum, a) => sum + (Number(a.price) || 0), 0) * 100) / 100;
    const baseSubtotal = Number(quote?.quoteSubtotal ?? quote?.price) || 0;
    const quoteVatRateForExtras = Number(quote?.quoteVatRate) || 0;
    const newSubtotal = Math.round((baseSubtotal + extrasTotal) * 100) / 100;
    const newVat = Math.round(newSubtotal * quoteVatRateForExtras * 100) / 100;
    const newTotal = Math.round((newSubtotal + newVat) * 100) / 100;
    const isRecurringQuote = !!(quote?.recurring && quote.recurring.pattern && quote.recurring.pattern !== 'none');
    // "Full payment upfront" quotes save the deposit as the quote price, so it grows with the extras
    const effectiveDeposit = (quote && Number(quote.depositRequired) > 0 && extrasTotal > 0 && Math.abs(Number(quote.depositRequired) - (Number(quote.price) || 0)) < 0.01)
        ? Math.round(((Number(quote.price) || 0) + extrasTotal) * 100) / 100
        : Number(quote?.depositRequired) || 0;

    const renderActionButtons = () => {
        if (actionOutcome === 'accepted') {
            // Check if the returned object reflects an active auto-booked state
            const isAutoBooked = ['unassigned', 'scheduled', 'active', 'pending_deposit'].includes(quote?.status);

            return (
                // ⭐ AESTHETIC UPGRADE: Glassmorphism success banner
                 <div className="p-8 text-center bg-green-50/80 dark:bg-green-900/20 backdrop-blur-sm border-2 border-green-200 dark:border-green-800 rounded-3xl animate-in zoom-in-95 duration-500">
                    <div className="w-16 h-16 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center mx-auto mb-4 shadow-inner">
                        <CheckCircle className="w-8 h-8 text-green-500" />
                    </div>
                    <h3 className="text-2xl font-black text-green-800 dark:text-green-300 mb-2 tracking-tight">Quote Accepted!</h3>
                    <p className="text-base font-medium text-green-700 dark:text-green-400 max-w-md mx-auto">
                        {isAutoBooked 
                            ? "Thank you! Your job has been automatically booked into our system."
                            : "Thank you. We will be in touch shortly to confirm the next steps."}
                    </p>
                </div>
            );
        }
        if (actionOutcome === 'declined') {
            return (
                // ⭐ AESTHETIC UPGRADE: Glassmorphism declined banner
                <div className="p-8 text-center bg-slate-50/80 dark:bg-slate-800/50 backdrop-blur-sm border-2 border-slate-200 dark:border-slate-700 rounded-3xl animate-in zoom-in-95 duration-500">
                    <div className="w-16 h-16 bg-slate-200 dark:bg-slate-700/50 rounded-full flex items-center justify-center mx-auto mb-4 shadow-inner">
                        <Info className="w-8 h-8 text-slate-500 dark:text-slate-400" />
                    </div>
                    <h3 className="text-2xl font-black text-slate-800 dark:text-white mb-2 tracking-tight">Quote Declined</h3>
                    <p className="text-base font-medium text-slate-600 dark:text-slate-400 max-w-md mx-auto">
                        You have declined this quote. Thank you for your consideration.
                    </p>
                </div>
            );
        }
        if (actionOutcome === 'expired' || (quote.quoteExpiryDate && new Date(quote.quoteExpiryDate) < new Date())) {
            return (
                // ⭐ AESTHETIC UPGRADE: Glassmorphism expired banner
                <div className="p-8 text-center bg-amber-50/80 dark:bg-amber-900/20 backdrop-blur-sm border-2 border-amber-200 dark:border-amber-800 rounded-3xl animate-in zoom-in-95 duration-500">
                    <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/50 rounded-full flex items-center justify-center mx-auto mb-4 shadow-inner">
                        <AlertTriangle className="w-8 h-8 text-amber-500" />
                    </div>
                    <h3 className="text-2xl font-black text-amber-800 dark:text-amber-300 mb-2 tracking-tight">Quote Expired</h3>
                    <p className="text-base font-medium text-amber-700 dark:text-amber-400 max-w-md mx-auto">
                        This quote is no longer available for a response. Please contact us for a new quote.
                    </p>
                </div>
            );
        }
        return (
            // ⭐ AESTHETIC UPGRADE: Modernized massive buttons
            <div className="pt-8 mt-4 border-t border-slate-100 dark:border-slate-800/50 flex flex-col gap-4">

                {/* ⭐ NEW: Quote Add-ons - optional extras the customer can tick before accepting */}
                {availableExtras.length > 0 && (
                    <div className="p-5 sm:p-6 bg-violet-50/70 dark:bg-violet-900/10 border border-violet-200 dark:border-violet-800/50 rounded-2xl">
                        <div className="flex items-center gap-3 mb-1">
                            <div className="p-2 bg-violet-100 dark:bg-violet-900/40 rounded-xl text-violet-600 dark:text-violet-400">
                                <Sparkles className="w-5 h-5" />
                            </div>
                            <h3 className="text-lg font-black text-slate-900 dark:text-white tracking-tight">Add Optional Extras</h3>
                        </div>
                        <p className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-4">Tick anything you would like adding{isRecurringQuote ? ' to each visit' : ''}. Prices are before VAT.</p>
                        <div className="space-y-2">
                            {availableExtras.map(extra => {
                                const checked = selectedAddOnIds.includes(String(extra._id));
                                return (
                                    <label key={extra._id} className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${checked ? 'bg-white dark:bg-slate-800 border-violet-400 dark:border-violet-600 shadow-sm' : 'bg-white/60 dark:bg-slate-800/40 border-transparent hover:border-violet-200 dark:hover:border-violet-800'}`}>
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => toggleAddOn(extra._id)}
                                            disabled={submitting}
                                            className="h-5 w-5 text-violet-600 focus:ring-violet-500 border-slate-300 rounded shrink-0"
                                        />
                                        <span className="flex-1 min-w-0">
                                            <span className="block font-bold text-slate-900 dark:text-white">{extra.name}</span>
                                            {extra.description && <span className="block text-sm text-slate-500 dark:text-slate-400">{extra.description}</span>}
                                        </span>
                                        <span className="font-black text-violet-700 dark:text-violet-300 whitespace-nowrap">+{formatCurrency(extra.price)}</span>
                                    </label>
                                );
                            })}
                        </div>
                        {extrasTotal > 0 && (
                            <div className="mt-4 pt-4 border-t border-violet-200 dark:border-violet-800/50 space-y-1">
                                <div className="flex justify-between text-sm font-semibold text-slate-600 dark:text-slate-400">
                                    <span>Extras</span>
                                    <span>+{formatCurrency(extrasTotal)}</span>
                                </div>
                                {newVat > 0 && (
                                    <div className="flex justify-between text-sm font-semibold text-slate-600 dark:text-slate-400">
                                        <span>VAT ({(quoteVatRateForExtras * 100).toFixed(quoteVatRateForExtras * 100 % 1 === 0 ? 0 : 2)}%)</span>
                                        <span>{formatCurrency(newVat)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between items-baseline">
                                    <span className="font-bold text-slate-900 dark:text-white">Your new total{isRecurringQuote ? ' (per visit)' : ''}</span>
                                    <span className="text-2xl font-black text-slate-900 dark:text-white">{formatCurrency(newTotal)}</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* ⭐ NEW: Deposit Warning Banner placed directly above the accept button */}
                {quote.depositRequired > 0 && (
                    <div className="p-5 bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 rounded-r-2xl shadow-sm flex items-start gap-4">
                        <div className="p-2 bg-amber-100 dark:bg-amber-800/50 rounded-full text-amber-600 dark:text-amber-400 shrink-0">
                            <CreditCard className="w-6 h-6" />
                        </div>
                        <div>
                            <h4 className="font-bold text-amber-900 dark:text-amber-100">Deposit Required to Book</h4>
                            <p className="text-sm font-medium text-amber-800 dark:text-amber-300 mt-1">
                                A secure deposit of <strong>{formatCurrency(effectiveDeposit)}</strong> is required to secure this booking. Upon clicking "Accept Quote", you will be safely redirected to Stripe to complete the payment.
                            </p>
                        </div>
                    </div>
                )}

                <div className="flex flex-col sm:flex-row justify-end gap-4">
                    <button
                        onClick={() => setIsDeclineModalOpen(true)}
                        className="w-full sm:w-auto px-8 py-4 border-2 border-red-200 dark:border-red-900/50 text-red-600 dark:text-red-400 font-bold rounded-2xl hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50 transition-all duration-200 flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-[0.98]"
                        disabled={submitting}
                    >
                        <X size={20} strokeWidth={2.5} /> Decline Quote
                    </button>
                    <button
                        onClick={handleAcceptQuote}
                        className="w-full sm:w-auto px-10 py-4 bg-green-500 hover:bg-green-600 text-white font-black rounded-2xl shadow-xl shadow-green-500/20 disabled:opacity-50 transition-all duration-200 flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-[0.98]"
                        disabled={submitting}
                    >
                        <Check size={20} strokeWidth={3} /> {submitting ? 'Processing...' : (extrasTotal > 0 ? `Accept for ${formatCurrency(newTotal)}` : 'Accept Quote')}
                    </button>
                </div>
            </div>
        );
    };

    const PageHeader = ({ title }) => {
        return (
            // ⭐ AESTHETIC UPGRADE: Blurred sticky header to match JobPhotosPage
            <header className="bg-white/80 dark:bg-slate-950/80 backdrop-blur-md border-b border-slate-200/50 dark:border-slate-800/50 shadow-sm sticky top-0 z-20">
                <div className="max-w-4xl mx-auto p-4 flex items-center">
                    {companySettings?.logoUrl ? (
                         <img src={companySettings.logoUrl} alt="Company Logo" className="h-10 w-auto mr-4 drop-shadow-sm" />
                    ) : (
                        <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl mr-3">
                            <FileText className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                        </div>
                    )}
                    <h1 className="text-xl font-black tracking-tight text-slate-800 dark:text-white">{title}</h1>
                </div>
            </header>
        );
    };
    
    // ⭐ AESTHETIC UPGRADE: Padded modern cards for detail items
    // ⭐ NEW: When value is an array (e.g. multiple services on a quote), render each entry as its
    // own pill instead of collapsing them into one raw "A + B" string.
    const DetailItem = ({ icon, label, value, children }) => (
        <div className="flex items-start p-5 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-700/50 transition-all duration-300 hover:shadow-md">
            <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl mr-4 flex-shrink-0">
                {React.cloneElement(icon, { className: "w-6 h-6 text-indigo-600 dark:text-indigo-400" })}
            </div>
            <div className="flex-1">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">{label}</p>
                {Array.isArray(value) ? (
                    <div className="flex flex-wrap gap-1.5 mt-1">
                        {value.map(v => (
                            <span key={v.label} className="px-2.5 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-sm font-bold rounded-full">
                                {v.label}{v.price !== null && v.price !== undefined ? ` — ${formatCurrency(v.price)}` : ''}
                            </span>
                        ))}
                    </div>
                ) : (
                    value && <p className="text-lg font-bold text-slate-900 dark:text-white">{value}</p>
                )}
                {children}
            </div>
        </div>
    );

    // ⭐ NEW: Split a quote's "+"-separated jobType string back into individual services, pairing
    // each one with its price from serviceLineItems when the quote was saved with per-service pricing
    const getJobTypeList = (jobType, serviceLineItems) => {
        const names = jobType ? String(jobType).split('+').map(jt => jt.trim()).filter(Boolean) : [];
        return names.map(name => {
            const match = Array.isArray(serviceLineItems) ? serviceLineItems.find(item => item.jobType === name) : null;
            return { label: name, price: match ? match.price : null };
        });
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-950">
                <Loader />
                <p className="mt-4 text-slate-500 dark:text-slate-400 font-medium animate-pulse">Loading quote details...</p>
            </div>
        );
    }

    if (error || !quote) {
        return (
            // ⭐ AESTHETIC UPGRADE: Center modal style error
            <div className="bg-slate-50 dark:bg-slate-950 min-h-screen font-sans flex flex-col">
                 <PageHeader title="Quote Details" />
                <main className="flex-1 flex items-center justify-center p-4">
                    <div className="w-full max-w-md bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl rounded-3xl shadow-2xl p-8 text-center border border-red-100 dark:border-red-900/50 animate-in zoom-in-95 duration-500">
                        <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                            <span className="text-3xl font-bold">!</span>
                        </div>
                        <p className="font-black text-2xl text-slate-900 dark:text-white mb-2 tracking-tight">Oops</p>
                        <p className="text-slate-500 dark:text-slate-400 font-medium">{error || "Quote details not found."}</p>
                    </div>
                </main>
                <ToastContainer position="bottom-right" theme="colored" />
            </div>
        );
    }
    
    const getFullAddress = (addr) => {
        if (!addr || typeof addr !== 'object' || Object.keys(addr).length === 0) {
            return null;
        }
        const addressParts = [
            addr.street,
            addr.city,
            addr.county,
            addr.postcode,
            addr.country
        ];
        const filteredParts = addressParts.filter(part => part && typeof part === 'string' && part.trim() !== '');
        if (filteredParts.length === 0) {
            return null;
        }
        return filteredParts.join(', ');
    };

    const fullAddress = getFullAddress(quote.siteAddress || quote.address);
    const uploadedDocuments = quote.uploadedDocuments || [];

    // ⭐ NEW: Determine smart labels and frequency display using the backend recurring object
    let freqDisplay = null;
    if (quote.recurring && quote.recurring.pattern && quote.recurring.pattern !== 'none') {
        const { pattern, interval } = quote.recurring;
        if (pattern === 'daily') {
            freqDisplay = 'Daily'; 
        } else if (pattern === 'weekly') {
            if (interval === 2) freqDisplay = 'Fortnightly';
            else if (interval === 4) freqDisplay = '4-Weekly';
            else if (interval === 8) freqDisplay = '8-Weekly';
            else freqDisplay = 'Weekly';
        } else if (pattern === 'monthly') {
            if (interval === 2) freqDisplay = 'Bi-Monthly';
            else freqDisplay = 'Monthly';
        }
    }
    
    const isRecurring = !!freqDisplay;
    const dateLabel = isRecurring ? "Preferred First Visit" : "Proposed Service Date";
    const timeLabel = isRecurring ? "Preferred Arrival Time" : "Proposed Time";
    
    return (
        <div className="bg-slate-50 dark:bg-slate-950 min-h-screen font-sans">
            <PageHeader title={`Quote ${quote.quoteNumber || ''}`} />
            <main className="p-4 sm:p-8 max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
                {/* ⭐ AESTHETIC UPGRADE: Glassmorphism rounded-3xl container */}
                <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl p-6 sm:p-10 rounded-3xl shadow-xl border border-slate-200 dark:border-slate-800 space-y-8 transition-all duration-300">
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        <DetailItem icon={<FileText />} label="Service" value={getJobTypeList(quote.jobType, quote.serviceLineItems)} />
                        
                        <DetailItem icon={getCurrencyIcon()} label={quote.payType === 'Hourly Rate' ? `Rate: ${formatCurrency(quote.price)} / hr` : "Estimated Price"}>
                            <p className="text-3xl font-black text-slate-900 dark:text-white mt-1">
                                {formatCurrency(quote.quoteTotal ?? quote.price)}
                                {freqDisplay && <span className="ml-2 text-lg font-bold text-indigo-500">({freqDisplay})</span>}
                            </p>
                            {(quote.quoteVatAmount > 0 || quote.payType === 'Hourly Rate') && (
                                <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700/60 space-y-1">
                                    <div className="flex justify-between text-xs font-semibold text-slate-500 dark:text-slate-400">
                                        <span>Subtotal</span>
                                        <span>{formatCurrency(quote.quoteSubtotal ?? quote.price)}</span>
                                    </div>
                                    {quote.quoteVatAmount > 0 && (
                                        <div className="flex justify-between text-xs font-semibold text-slate-500 dark:text-slate-400">
                                            <span>VAT ({((quote.quoteVatRate || 0) * 100).toFixed((quote.quoteVatRate || 0) * 100 % 1 === 0 ? 0 : 2)}%)</span>
                                            <span>{formatCurrency(quote.quoteVatAmount)}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </DetailItem>

                        {quote.date && (
                            <DetailItem 
                                icon={<Calendar />} 
                                label={dateLabel} 
                                value={formatDate(quote.date)}
                            />
                        )}
                        {quote.time && (
                            <DetailItem 
                                icon={<Clock />} 
                                label={timeLabel} 
                                value={quote.time}
                            />
                        )}
                        {quote.duration && (
                            <DetailItem 
                                icon={<Timer />} 
                                label="Estimated Duration" 
                                value={formatDuration(quote.duration)}
                            />
                        )}
                        {fullAddress && (
                            <div className="sm:col-span-2">
                                <DetailItem
                                    icon={<MapPin />}
                                    label="Job Address"
                                    value={fullAddress}
                                />
                            </div>
                        )}
                        {quote.quoteExpiryDate && (
                            <DetailItem icon={<Calendar />} label="Expires On" value={formatDate(quote.quoteExpiryDate)} />
                        )}
                    </div>

                    <div className="p-6 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-700/50">
                        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Description</h3>
                        <p className="text-slate-800 dark:text-slate-200 whitespace-pre-wrap leading-relaxed font-medium">
                            {quote.description || 'No description provided.'}
                        </p>
                    </div>

                    {uploadedDocuments.length > 0 && (
                        <div className="pt-6 border-t border-slate-100 dark:border-slate-800/50">
                            <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-4 flex items-center">
                                <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl mr-3">
                                    <FileText className="w-5 h-5 text-indigo-600 dark:text-indigo-400" /> 
                                </div>
                                Supporting Documents
                            </h3>
                            <div className="space-y-3">
                                {uploadedDocuments.map((doc) => (
                                    <a 
                                        key={doc.fileUrl} 
                                        href={doc.fileUrl} 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        // ⭐ AESTHETIC UPGRADE: Padded pill-like document links
                                        className="flex items-center justify-between p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl hover:border-indigo-300 dark:hover:border-indigo-700 hover:shadow-md transition duration-200 group"
                                    >
                                        <span className="flex items-center truncate text-sm font-semibold text-slate-800 dark:text-slate-200">
                                            <div className="p-2 bg-slate-100 dark:bg-slate-700 rounded-lg mr-3 group-hover:bg-indigo-50 dark:group-hover:bg-indigo-900/30 transition-colors">
                                                <Download className="w-4 h-4 text-indigo-500" />
                                            </div>
                                            <span className="truncate">{doc.fileName}</span>
                                            <span className="ml-2 text-xs font-bold text-slate-400 uppercase tracking-wider">
                                                ({doc.mimeType?.split('/')[1] || 'FILE'})
                                            </span>
                                        </span>
                                        <div className="w-8 h-8 rounded-full bg-slate-50 dark:bg-slate-700 flex items-center justify-center group-hover:bg-indigo-100 dark:group-hover:bg-indigo-900/50 transition-colors">
                                            <Download className="w-4 h-4 text-slate-400 group-hover:text-indigo-600" />
                                        </div>
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}

                    {renderActionButtons()}
                </div>
            </main>

            <Modal
                isOpen={isDeclineModalOpen}
                onClose={() => setIsDeclineModalOpen(false)}
                title="Decline Quote"
            >
                {/* ⭐ AESTHETIC UPGRADE: Styled the content inside the Modal */}
                <div className="p-6 sm:p-8 bg-white dark:bg-slate-900">
                    <p className="mb-4 font-medium text-slate-500 dark:text-slate-400">Please provide a brief reason for declining this quote (optional):</p>
                    <textarea
                        value={declineReason}
                        onChange={(e) => setDeclineReason(e.target.value)}
                        rows="4"
                        className="w-full p-4 border-2 border-slate-200 dark:border-slate-700 rounded-2xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white transition-all font-medium resize-none"
                        placeholder="e.g., Price too high, chose another provider..."
                        disabled={submitting}
                    ></textarea>
                </div>
                 <div className="bg-slate-50 dark:bg-slate-800/80 px-6 py-5 flex flex-col sm:flex-row justify-end gap-3 border-t border-slate-200 dark:border-slate-700">
                    <button 
                        onClick={() => setIsDeclineModalOpen(false)} 
                        className="w-full sm:w-auto px-6 py-3 font-semibold rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors" 
                        disabled={submitting}
                    >
                        Cancel
                    </button>
                    <button 
                        onClick={handleDeclineSubmit} 
                        className="w-full sm:w-auto px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl shadow-lg shadow-red-500/20 transition-all hover:scale-[1.02] active:scale-[0.98]" 
                        disabled={submitting}
                    >
                        {submitting ? 'Submitting...' : 'Confirm Decline'}
                    </button>
                </div>
            </Modal>
             <ToastContainer position="bottom-right" theme="colored" />
        </div>
    );
};

export default PublicQuotePage;